import type { IUsePriceProductDuplicateResult } from '../hooks/usePriceProductDuplicate';
import type { IUsePriceProductBroadcastResult } from '../hooks/usePriceProductBroadcast';
import type { IUsePriceProductSelectionResult } from '../hooks/usePriceProductSelection';
import type { IUsePriceProductQuickFillResult } from '../hooks/usePriceProductQuickFill';
import type { IUsePriceProductExpandResult } from '../hooks/usePriceProductExpand';
import type { IUsePriceProductDraftResult } from '../hooks/usePriceProductDraft';
import type { IUsePriceProductRowsResult } from '../hooks/usePriceProductRows';
import type { IUsePriceProductSyncResult } from '../hooks/usePriceProductSync';
import type { IPriceProductDraft } from '../types';

import { usePriceProductDuplicate } from '../hooks/usePriceProductDuplicate';
import { usePriceProductBroadcast } from '../hooks/usePriceProductBroadcast';
import { usePriceProductSelection } from '../hooks/usePriceProductSelection';
import { usePriceProductQuickFill } from '../hooks/usePriceProductQuickFill';
import { toControlVariantBaseIDs, toExpandableBaseIDs } from '../utils';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { usePriceProductExpand } from '../hooks/usePriceProductExpand';
import { useCampaignPriceSetupGuideContext } from '../../../contexts';
import { usePriceProductDraft } from '../hooks/usePriceProductDraft';
import { usePriceProductRows } from '../hooks/usePriceProductRows';
import { usePriceProductSync } from '../hooks/usePriceProductSync';
import { ESetupGuideCampaignPrice } from '../../../types';
import { useCampaignContext } from '~/modules/campaign';

interface IProps {
  children: React.ReactNode;
}

interface IPriceProductTableContext
  extends
    Omit<IUsePriceProductRowsResult, 'handleRefreshProducts'>,
    Omit<IUsePriceProductDraftResult, 'getDraft' | 'handleApplyDraftPreview'>,
    IUsePriceProductDuplicateResult,
    IUsePriceProductBroadcastResult,
    IUsePriceProductSelectionResult,
    IUsePriceProductExpandResult,
    Omit<IUsePriceProductQuickFillResult, 'handleResetQuickFill'>,
    IUsePriceProductSyncResult {
  /** The typed amounts with any open quick-fill rule laid over them — what a price field renders. */
  getDraft: (baseID: string) => IPriceProductDraft;
  /**
   * Only the amounts held on the campaign — what Save sends. An open quick-fill rule is left out until
   * Apply, so a gate reading this can't pass on a preview the server never receives.
   */
  getSavedDraft: (baseID: string) => IPriceProductDraft;
  isDisabledEditing: boolean;
}

const PriceProductTableContext = createContext<IPriceProductTableContext | null>(null);

export const usePriceProductTableContext = (): IPriceProductTableContext => {
  const context = useContext(PriceProductTableContext);
  if (!context)
    throw new Error('usePriceProductTableContext must be used within a PriceProductTableProvider');

  return context;
};

export const PriceProductTableProvider: React.FC<IProps> = ({ children }) => {
  const { isDisabledEditing } = useCampaignContext();
  const {
    rows,
    pickedProducts,
    isLoadingRows,
    handlePickProducts: handlePickProductsBase,
    handleRemoveProduct: handleRemoveProductBase,
    handleRefreshProducts,
  } = usePriceProductRows();
  const {
    getDraft,
    hasAnyDraftPrice,
    handleChangeDraft: handleChangeDraftBase,
    handleChangeDrafts: handleChangeDraftsBase,
    handleApplyDraftPreview: handleApplyDraftPreviewBase,
  } = usePriceProductDraft();
  const { isSyncingControlPrices, handleRefreshControlPrices } = usePriceProductSync({
    onRefresh: handleRefreshProducts,
  });
  const { handleBroadcastFocus, handleBroadcastBlur, isBroadcastTarget } =
    usePriceProductBroadcast();
  const { setupGuideSteps, handleUpdateSetupGuide } = useCampaignPriceSetupGuideContext();

  // Typing an amount or applying a quick-fill rule both count as the merchant having edited the
  // Variant price — neither waits for a save, which can be a while off yet. Guarded so the mutation
  // only fires once rather than on every keystroke.
  const isEditVariantPriceDone = !!setupGuideSteps.find(
    (step) => step.id === ESetupGuideCampaignPrice.EDIT_VARIANT_PRICE,
  )?.completed;
  const handleMarkVariantPriceEdited = useCallback(() => {
    if (isEditVariantPriceDone) return;

    handleUpdateSetupGuide([ESetupGuideCampaignPrice.EDIT_VARIANT_PRICE]);
  }, [isEditVariantPriceDone, handleUpdateSetupGuide]);

  const handleChangeDraft = useCallback(
    (change: Parameters<typeof handleChangeDraftBase>[0]) => {
      handleChangeDraftBase(change);
      handleMarkVariantPriceEdited();
    },
    [handleChangeDraftBase, handleMarkVariantPriceEdited],
  );
  const handleChangeDrafts = useCallback(
    (change: Parameters<typeof handleChangeDraftsBase>[0]) => {
      handleChangeDraftsBase(change);
      handleMarkVariantPriceEdited();
    },
    [handleChangeDraftsBase, handleMarkVariantPriceEdited],
  );
  const handleApplyDraftPreview = useCallback(
    (preview: Parameters<typeof handleApplyDraftPreviewBase>[0]) => {
      handleApplyDraftPreviewBase(preview);
      handleMarkVariantPriceEdited();
    },
    [handleApplyDraftPreviewBase, handleMarkVariantPriceEdited],
  );

  const controlVariantBaseIDs = useMemo(() => toControlVariantBaseIDs(rows), [rows]);
  const expandableBaseIDs = useMemo(() => toExpandableBaseIDs(rows), [rows]);

  const { isDuplicatedProduct } = usePriceProductDuplicate();

  const {
    selectedBaseIDs,
    isAllSelected,
    isAnySelected,
    handleToggleSelectedGroup,
    handleToggleSelected,
    handleToggleSelectAll,
  } = usePriceProductSelection({ controlVariantBaseIDs });
  const { isExpanded, isAnyExpanded, handleToggleExpand, handleCollapseAll } =
    usePriceProductExpand({
      expandableBaseIDs,
    });
  const {
    isQuickFillOpen,
    quickFillRules,
    quickFillPreview,
    handleOpenQuickFill,
    handleChangeQuickFillRule,
    handleApplyQuickFill,
    handleResetQuickFill,
  } = usePriceProductQuickFill({ rows, selectedBaseIDs, onApply: handleApplyDraftPreview });

  // A rule left open would carry over onto the next product's variants and fill fields the merchant
  // never typed into, so swapping or removing the product starts the table from blank.
  const handlePickProducts = useCallback(
    (items: Parameters<typeof handlePickProductsBase>[0]) => {
      handleResetQuickFill();
      handlePickProductsBase(items);
    },
    [handlePickProductsBase, handleResetQuickFill],
  );
  const handleRemoveProduct = useCallback(
    (baseID: string) => {
      handleResetQuickFill();
      handleRemoveProductBase(baseID);
    },
    [handleRemoveProductBase, handleResetQuickFill],
  );

  // The preview wins over what is typed while the rule bar is open, and holds nothing once it closes,
  // so the fields fall back to the merchant's own amounts without either side having to clear them.
  const getPreviewedDraft = useCallback(
    (baseID: string) => ({ ...getDraft(baseID), ...quickFillPreview[baseID] }),
    [getDraft, quickFillPreview],
  );

  const value = useMemo<IPriceProductTableContext>(
    () => ({
      rows,
      pickedProducts,
      isLoadingRows,
      handlePickProducts,
      handleRemoveProduct,
      isSyncingControlPrices,
      handleRefreshControlPrices,
      handleBroadcastFocus,
      handleBroadcastBlur,
      isBroadcastTarget,
      isDuplicatedProduct,
      getDraft: getPreviewedDraft,
      getSavedDraft: getDraft,
      hasAnyDraftPrice,
      handleChangeDraft,
      handleChangeDrafts,
      selectedBaseIDs,
      isAllSelected,
      isAnySelected,
      handleToggleSelectedGroup,
      handleToggleSelected,
      handleToggleSelectAll,
      isExpanded,
      isAnyExpanded,
      handleToggleExpand,
      handleCollapseAll,
      isQuickFillOpen,
      quickFillRules,
      quickFillPreview,
      handleOpenQuickFill,
      handleChangeQuickFillRule,
      handleApplyQuickFill,
      isDisabledEditing,
    }),
    [
      rows,
      pickedProducts,
      isLoadingRows,
      handlePickProducts,
      handleRemoveProduct,
      isSyncingControlPrices,
      handleRefreshControlPrices,
      handleBroadcastFocus,
      handleBroadcastBlur,
      isBroadcastTarget,
      isDuplicatedProduct,
      getPreviewedDraft,
      getDraft,
      hasAnyDraftPrice,
      handleChangeDraft,
      handleChangeDrafts,
      selectedBaseIDs,
      isAllSelected,
      isAnySelected,
      handleToggleSelectedGroup,
      handleToggleSelected,
      handleToggleSelectAll,
      isExpanded,
      isAnyExpanded,
      handleToggleExpand,
      handleCollapseAll,
      isQuickFillOpen,
      quickFillRules,
      quickFillPreview,
      handleOpenQuickFill,
      handleChangeQuickFillRule,
      handleApplyQuickFill,
      isDisabledEditing,
    ],
  );

  return (
    <PriceProductTableContext.Provider value={value}>{children}</PriceProductTableContext.Provider>
  );
};
